import { isFeedCable } from "./busbar-conversion";
import { isAcTerminalId } from "./ac";
import { getDefinition, type DefLookup } from "./catalog";
import { effectiveTerminals } from "./effective";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  System,
  Terminal,
} from "./types";

/**
 * Swap the part a node *is*, keeping everything wired to it. Cables re-land a
 * *group* at a time — grouped by the terminal they already share, so a shared
 * stud stays shared and six fuse slots stay six. If any group has nowhere to
 * go the whole swap is refused rather than partially applied.
 */

export type ComponentReplacePlan = {
  node: ComponentNode;
  /** The **full** cable list, with this node's endpoints re-landed. */
  updatedCables: Cable[];
};

/** Null when the node isn't a component, the target doesn't resolve or is what
 *  it already is, or a cable has no terminal of its polarity to land on. */
export function planComponentReplace(
  system: System,
  nodeId: string,
  definitionId: string,
  defLookup: DefLookup = getDefinition,
): ComponentReplacePlan | null {
  const old = system.nodes.find((n) => n.id === nodeId);
  if (!old || old.kind !== "component") return null;
  const oldDef = defLookup(old.definitionId, old.customDefinition);
  const newDef = defLookup(definitionId);
  if (!oldDef || !newDef) return null;
  // `definitionId` is `"custom"` on every custom node, so "already this part"
  // has to compare the resolved definition too.
  if (oldDef.id === newDef.id && old.definitionId === definitionId) return null;

  const groups = groupByTerminal(system, nodeId, oldDef, old, defLookup);

  const node = carryOver(old, oldDef, newDef, groups);
  const newTerminals = effectiveTerminals(newDef, node);

  const claimed = new Set<string>();
  const landing = new Map<string, string>(); // old terminal id → new terminal id
  for (const group of groups) {
    const pick = oldDef.requestedModel
      ? pickPlaceholderLanding(newDef, newTerminals, group, claimed)
      : pickLanding(newDef, newTerminals, group, claimed);
    if (!pick) return null;
    claimed.add(pick.id);
    landing.set(group.terminalId, pick.id);
  }

  const updatedCables = system.cables.map((cable) => {
    const from =
      cable.fromNodeId === nodeId ? landing.get(cable.fromTerminalId) : undefined;
    const to =
      cable.toNodeId === nodeId ? landing.get(cable.toTerminalId) : undefined;
    if (from === undefined && to === undefined) return cable;
    return {
      ...cable,
      ...(from === undefined ? {} : { fromTerminalId: from }),
      ...(to === undefined ? {} : { toTerminalId: to }),
    };
  });

  return { node, updatedCables };
}

/** The cables landing on one of the old part's terminals. */
type TerminalGroup = {
  terminalId: string;
  /** The old terminal, when the definition still declares it. */
  terminal: Terminal | undefined;
  cables: number;
  /** True when any cable here brings power *in*. */
  isFeed: boolean;
};

/** Feeds sort first, and that ordering is what puts the incoming supply on the
 *  new part's input post and leaves the fused outputs for the loads. */
function groupByTerminal(
  system: System,
  nodeId: string,
  oldDef: ComponentDefinition,
  old: ComponentNode,
  defLookup: DefLookup = getDefinition,
): TerminalGroup[] {
  const oldTerminals = effectiveTerminals(oldDef, old);
  const order = new Map(oldTerminals.map((t, i) => [t.id, i]));
  const byTerminal = new Map<string, TerminalGroup>();

  const add = (terminalId: string, isFeed: boolean) => {
    const existing = byTerminal.get(terminalId);
    if (existing) {
      existing.cables += 1;
      existing.isFeed = existing.isFeed || isFeed;
      return;
    }
    byTerminal.set(terminalId, {
      terminalId,
      terminal: oldTerminals.find((t) => t.id === terminalId),
      cables: 1,
      isFeed,
    });
  };

  for (const cable of system.cables) {
    // A jumper across one part's own posts touches this node twice.
    if (cable.fromNodeId === nodeId) {
      add(cable.fromTerminalId, feedsInto(system, cable, nodeId, defLookup));
    }
    if (cable.toNodeId === nodeId) {
      add(cable.toTerminalId, feedsInto(system, cable, nodeId, defLookup));
    }
  }

  return [...byTerminal.values()].sort(
    (a, b) =>
      Number(b.isFeed) - Number(a.isFeed) ||
      (order.get(a.terminalId) ?? Infinity) - (order.get(b.terminalId) ?? Infinity),
  );
}

function feedsInto(
  system: System,
  cable: Cable,
  nodeId: string,
  defLookup: DefLookup,
): boolean {
  const otherId = cable.fromNodeId === nodeId ? cable.toNodeId : cable.fromNodeId;
  const other = system.nodes.find((n) => n.id === otherId);
  if (!other || other.kind !== "component") return false;
  const otherDef = defLookup(other.definitionId, other.customDefinition);
  if (!otherDef) return false;
  const otherTerminalId =
    cable.fromNodeId === nodeId ? cable.toTerminalId : cable.fromTerminalId;
  const terminal = effectiveTerminals(otherDef, other).find(
    (t) => t.id === otherTerminalId,
  );
  return isFeedCable(otherDef.category, terminal?.direction);
}

/** Catalog parts this node could swap to: same category, and only ones every
 *  attached cable can land on. */
export function replacementOptions(
  system: System,
  nodeId: string,
  catalog: ComponentDefinition[],
  defLookup: DefLookup = getDefinition,
): ComponentDefinition[] {
  const node = system.nodes.find((n) => n.id === nodeId);
  if (!node || node.kind !== "component") return [];
  const def = defLookup(node.definitionId, node.customDefinition);
  if (!def) return [];
  return catalog.filter(
    (candidate) =>
      candidate.category === def.category &&
      (candidate.id === def.id ||
        planComponentReplace(system, nodeId, candidate.id, defLookup) !== null),
  );
}

/** Instance state that survives the swap: the installation, never the old definition. */
function carryOver(
  old: ComponentNode,
  oldDef: ComponentDefinition,
  newDef: ComponentDefinition,
  groups: TerminalGroup[],
): ComponentNode {
  const node: ComponentNode = {
    id: old.id,
    kind: "component",
    definitionId: newDef.id,
    position: old.position,
  };
  if (old.parentId) node.parentId = old.parentId;
  if (old.notes) node.notes = old.notes;
  if (old.rotation) node.rotation = old.rotation;
  // A label echoing the old part's name would caption the new part wrongly.
  if (old.label && old.label !== oldDef.name && old.label !== oldDef.shortName) {
    node.label = old.label;
  }
  // Duty assumptions describe how a load is used, so they survive load → load only.
  if (oldDef.category === newDef.category && newDef.category === "load") {
    if (old.dutyHoursPerDayOverride !== undefined) {
      node.dutyHoursPerDayOverride = old.dutyHoursPerDayOverride;
    }
    if (old.alwaysOnOverride !== undefined) {
      node.alwaysOnOverride = old.alwaysOnOverride;
    }
  }
  // Studs are synthesized from this count: one group claims the input post, so
  // the rest need a stud each.
  if (newDef.renderVariant === "busbar") {
    node.studCount = Math.max(1, old.studCount ?? 1, groups.length - 1);
  }
  // An MRBF bolted to a battery post stays with the battery it protects.
  if (old.batteryFuse && newDef.category === "battery") {
    node.batteryFuse = old.batteryFuse;
  }
  // Swapping keeps the installed fuse declaration. Older drawings may carry
  // its rating only on a connected cable; a palette default would conflict.
  if (old.fuseAmp !== undefined && newDef.isFuseBlock) node.fuseAmp = old.fuseAmp;
  if (old.isFusebox && newDef.renderVariant === "busbar") node.isFusebox = true;
  // Drop settings the new part doesn't declare — `getSetting` would read a
  // leftover key as a value the user chose on a part that never offered it.
  if (old.settings && newDef.settings?.length) {
    const declared = new Set(newDef.settings.map((s) => s.id));
    const kept = Object.entries(old.settings).filter(([id]) => declared.has(id));
    if (kept.length) node.settings = Object.fromEntries(kept);
  }
  // Deliberately not carried: `terminalOverrides` (ids are gone),
  // `customDefinition`, `provisional`, `partEstimated`.
  return node;
}

/**
 * Where one group of cables lands on the new part. Same polarity is
 * non-negotiable; direction is a preference *except* on an output — a branch
 * that falls back onto a block's shared input post reads as protected when it
 * isn't. An unresolvable old terminal takes any free one.
 */
function pickLanding(
  def: ComponentDefinition,
  terminals: Terminal[],
  group: TerminalGroup,
  claimed: Set<string>,
): Terminal | null {
  const free = terminals.filter((t) => !claimed.has(t.id));
  if (!free.length) return null;
  const old = group.terminal;
  if (!old) return free[0];

  let sameRail = free.filter((t) => t.polarity === old.polarity);
  // A group arrived sharing a post, so it needs a shareable one.
  if (group.cables > 1) sameRail = sameRail.filter((t) => !isExclusive(def, t));
  if (!sameRail.length) return null;

  const outputs = sameRail.filter((t) => t.direction === "output");
  if (old.direction === "output" && outputs.length > 0) return outputs[0];

  return (
    sameRail.find((t) => t.direction === old.direction) ??
    sameRail.find((t) => t.direction === "bidi") ??
    sameRail[0]
  );
}

/** A fuse block's output slot is one fuse, so two circuits may not share it. */
function isExclusive(def: ComponentDefinition, t: Terminal): boolean {
  return def.isFuseBlock === true && t.direction === "output";
}

/** A placeholder is an unverified generic topology. Never guess which of
 * multiple ports the user meant, turn an input into an output, or silently
 * reassign an edited signal connector. The user can rewire ambiguous terminals
 * explicitly before selecting the catalog part. Ordinary swaps keep their
 * existing behavior; both suggested and manual placeholder swaps use this gate. */
function pickPlaceholderLanding(
  def: ComponentDefinition,
  terminals: Terminal[],
  group: TerminalGroup,
  claimed: Set<string>,
): Terminal | null {
  const old = group.terminal;
  if (!old || old.polarity === "signal") return null;
  const candidates = terminals.filter(terminal =>
    !claimed.has(terminal.id) &&
    terminal.polarity === old.polarity &&
    terminal.direction === old.direction &&
    isAcTerminalId(terminal.id) === isAcTerminalId(old.id) &&
    (group.cables <= 1 || !isExclusive(def, terminal)),
  );
  const sameId = candidates.find(terminal => terminal.id === old.id);
  if (sameId) return sameId;
  // Neutral represents either an AC conductor or either DC rail; its function
  // cannot be inferred safely merely because only one free terminal remains.
  if (old.polarity === "neutral") return null;
  return candidates.length === 1 ? candidates[0] : null;
}
